import { useEffect, useState } from "react";
import { X, Check, Copy } from "lucide-react";
import { api } from "@/lib/api";

export default function InviteFriendModal({ open, onClose, onSent }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(null); // { email, share_url }
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setName("");
      setEmail("");
      setErr("");
      setSent(null);
      setCopied(false);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/referrals/invite", {
        name: name.trim(),
        email: email.trim(),
      });
      setSent({ email: email.trim(), share_url: data?.share_url || "" });
      onSent?.();
    } catch (e) {
      setErr(
        e?.response?.data?.detail ||
          e?.message ||
          "We couldn't send the invitation. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!sent?.share_url) return;
    try {
      await navigator.clipboard.writeText(sent.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="invite-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-neutral-500 hover:text-black"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {sent ? (
          <>
            <h2 className="font-display text-xl font-bold tracking-tight mb-2">
              Invitation sent
            </h2>
            <p className="text-[13px] text-neutral-700 leading-relaxed">
              We've emailed your personal referral link to{" "}
              <span className="font-semibold text-neutral-900">{sent.email}</span>.
            </p>
            <p className="text-[13px] text-neutral-700 leading-relaxed mt-3">
              Planning will resume automatically once they accept the invitation
              and add at least one loyalty program to their account.
            </p>
            {sent.share_url && (
              <div className="mt-5">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
                  Your referral link
                </div>
                <div className="flex items-center gap-2 border border-black/10 rounded-md bg-neutral-50 px-3 py-2">
                  <span className="flex-1 truncate text-[12.5px] font-mono text-neutral-700">
                    {sent.share_url}
                  </span>
                  <button
                    type="button"
                    onClick={copyLink}
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1 rounded-md bg-black text-white hover:bg-neutral-800 transition-colors"
                    data-testid="invite-copy-link"
                  >
                    {copied ? (
                      <>
                        <Check size={11} /> Copied
                      </>
                    ) : (
                      <>
                        <Copy size={11} /> Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
            <div className="mt-6 flex items-center justify-end">
              <button
                type="button"
                onClick={onClose}
                className="btn-primary text-[13px] py-1.5 px-4"
                data-testid="invite-close"
              >
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="font-display text-xl font-bold tracking-tight mb-1">
              Invite a friend
            </h2>
            <p className="text-[13px] text-neutral-600 mb-5 leading-relaxed">
              Reservoir is currently invitation-only. Enter your friend's name
              and email and we'll send them your personal sign-up link. Once
              they accept and add a loyalty program, your planning will
              resume.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-[12px] font-semibold text-neutral-700 mb-1">
                  Their name
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input w-full text-[13px]"
                  placeholder="Alex Kim"
                  disabled={submitting}
                  data-testid="invite-name"
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-neutral-700 mb-1">
                  Their email
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input w-full text-[13px]"
                  placeholder="alex@example.com"
                  disabled={submitting}
                  data-testid="invite-email"
                />
              </div>
              {err && (
                <div className="text-[13px] text-red-600" data-testid="invite-error">
                  {err}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn text-[13px] py-1.5 px-3"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !name.trim() || !email.trim()}
                  className="btn-primary text-[13px] py-1.5 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="invite-submit"
                >
                  {submitting ? "Sending…" : "Send invitation"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
